import {
  CourseSubject,
  ExamBoards,
  ExamLevel,
  ExamResourcesDifficulty,
  ICreateExamQuestion,
  IMeilisearchExamQuestion,
  QuestionFormat,
  ResourceType,
} from '../types'
import {
  erUploadBoardsAllowed,
  erUploadDifficultiesAllowed,
  erUploadLevelsAllowed,
  erUploadQuestionFormatsAllowed,
  erUploadResourceTypesAllowed,
  erUploadSubjectsAllowed,
} from './exam-resources'

export const dataSheetGridOnChangeExamQuestionsValidation = (
  value: Record<string, any>[],
): string | undefined => {
  for (const row of value) {
    // Subject validation
    if (!erUploadSubjectsAllowed.includes(row.subject)) {
      console.error(
        `Subject field from spreadsheet is not matching predefined Subject values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadSubjectsAllowed),
        { value: row.subject },
      )

      return `Subject: ${row.subject} not recognized by the system!`
    }

    // Exam Board validation
    if (!erUploadBoardsAllowed.includes(row.examBoard)) {
      console.error(
        `Exam Board field from spreadsheet is not matching predefined Exam Board values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadBoardsAllowed),
        { value: row.examBoard },
      )

      return `Exam Board: ${row.examBoard} not recognized by the system!`
    }

    // Exam Level validation
    if (!erUploadLevelsAllowed.includes(row.examLevel)) {
      console.error(
        `Exam Level field from spreadsheet is not matching predefined Exam Level values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadLevelsAllowed),
        { value: row.examLevel },
      )
      return `Exam Level: ${row.examLevel} not recognized by the system!`
    }

    // Difficulty validation
    if (!erUploadDifficultiesAllowed.includes(row.difficulty)) {
      console.error(
        `Difficulty field from spreadsheet is not matching predefined Difficulty values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadDifficultiesAllowed),
        { value: row.difficulty },
      )
      return `Difficulty: ${row.difficulty} not recognized by the system!`
    }

    // Resource Type validation
    if (!erUploadResourceTypesAllowed.includes(row.resourceType)) {
      console.error(
        `Resource Type field from spreadsheet is not matching predefined Resource Type values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadResourceTypesAllowed),
        { value: row.resourceType },
      )
      return `Resource Type: ${row.resourceType} not recognized by the system!`
    }

    // Question Format validation
    if (!erUploadQuestionFormatsAllowed.includes(row.questionFormat)) {
      console.error(
        `Question Format field from spreadsheet is not matching predefined Question Format values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(erUploadQuestionFormatsAllowed),
        { value: row.questionFormat },
      )
      return `Question Format: ${row.questionFormat} not recognized by the system!`
    }

    // calculator should be either "yes" or "no"
    if (
      !!row.calculator &&
      row.calculator !== 'yes' &&
      row.calculator !== 'no'
    ) {
      console.error(
        `Calculator field from spreadsheet is not matching predefined Calculator values. This field is case sensitive, please use one of values allowed: `,
        JSON.stringify(['yes', 'no']),
        { value: row.calculator },
      )
      return "Calculator must be either 'yes' or 'no'!"
    }

    // check topics is array
    if (!Array.isArray(row.topics)) {
      const errorMessage = `Invalid value for field 'topics' in question: ${row.questionNo}`
      console.error(errorMessage, { value: row.topics })

      return errorMessage
    }

    // check topics is not empty array
    if (!row.topics.length) {
      const errorMessage = `Empty Topics field is not allowed for question: ${row.questionNo}`
      console.error(errorMessage, { value: row.topics })

      return errorMessage
    }

    // check topics is array of strings
    if (!row.topics.every((item) => typeof item === 'string')) {
      const errorMessage = `Invalid value for field 'topics' in question: ${row.questionNo}`
      console.error(errorMessage, { value: row.topics })

      return errorMessage
    }

    // check if each topic is valid
    for (const topic of row.topics) {
      if (!checkDpTopicNameIsValid(topic)) {
        const errorMessage = `Invalid topic name for question: ${row.questionNo}`
        console.error(errorMessage, { value: row.topics, topic })

        return errorMessage
      }
    }

    // check marks is not empty
    if (!row.marks) {
      console.error(
        `Marks field is required for row: ${row.paperName} question: ${row.questionNo}`,
        {
          row,
        },
      )
      return 'Marks is required!'
    }
    const marksValue = Number(row.marks)

    if (isNaN(marksValue) || marksValue <= 0) {
      const errorMessage = `Invalid marks for question: ${row.questionNo}`
      console.error(errorMessage, { value: row.marks })

      return errorMessage
    }

    if (!row.questionNo) {
      console.error(
        `Question No field is required for row: ${row.paperName} question: ${row.questionNo}`,
        {
          row,
        },
      )
      return 'Question No is required!'
    }

    const numValue = Number(row.questionNo)
    if (isNaN(numValue) || numValue <= 0) {
      const errorMessage = `Invalid question number for question: ${row.questionNo}`
      console.error(errorMessage, { value: row.questionNo })

      return errorMessage
    }

    if (!row.paperName) {
      console.error(
        `Paper Name field is required for row: ${row.paperName} question: ${row.questionNo}`,
        {
          row,
        },
      )
      return 'Paper Name is required!'
    }
  }

  return undefined
}

const emptyValueAllowedKeys = ['paperDate']

export function uploadExamQuestionsBeforeSaveValidation(
  questions: ICreateExamQuestion[],
): boolean {
  return questions.every((question) => {
    // check top-level fields of ICreateExamPaper
    for (const key in question) {
      // check for allowed empty value field
      if (emptyValueAllowedKeys.includes(key)) {
        continue
      }

      const value = question[key as keyof ICreateExamQuestion]

      // check for empty value field
      if (value === null || value === undefined) {
        const errorMessage = `Missing value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
        console.error(errorMessage)

        throw new Error(errorMessage)
      }

      // check key 'id' is empty or an empty string
      if (
        key === 'id' &&
        (value === '' || value === null || value === undefined)
      ) {
        const errorMessage = `Invalid or empty 'id' value in resource: ${question.paperName} question: ${question.questionNo}`
        console.error(errorMessage, { value })

        throw new Error(errorMessage)
      }

      if (key === 'questionNo') {
        const numValue = Number(value)
        if (isNaN(numValue) || numValue <= 0) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }

      if (key === 'paperName') {
        // check is string
        if (typeof value !== 'string') {
          const errorMessage = `Invalid value type for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
        // check is not empty string
        if (!value.length) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }

      if (key === 'topics') {
        // check is array
        if (!Array.isArray(value)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
        // check is empty array
        if (!value.length) {
          const errorMessage = `Missing value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
        // check is array of strings
        if (!value.every((item) => typeof item === 'string')) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }

        // check if each topic is valid
        for (const topic of value) {
          if (!checkDpTopicNameIsValid(topic)) {
            const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
            console.error(errorMessage, { value })

            throw new Error(errorMessage)
          }
        }
      }

      if (key === 'subject') {
        if (!erUploadSubjectsAllowed.includes(value as CourseSubject)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, JSON.stringify(erUploadSubjectsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }

      if (key === 'examBoard') {
        if (!erUploadBoardsAllowed.includes(value as ExamBoards)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, JSON.stringify(erUploadBoardsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }

      if (key === 'examLevel') {
        if (!erUploadLevelsAllowed.includes(value as ExamLevel)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, JSON.stringify(erUploadLevelsAllowed), {
            value,
          })

          throw new Error(errorMessage)
        }
      }

      if (key === 'difficulty') {
        if (
          !erUploadDifficultiesAllowed.includes(
            value as ExamResourcesDifficulty,
          )
        ) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(
            errorMessage,
            JSON.stringify(erUploadDifficultiesAllowed),
            {
              value,
            },
          )

          throw new Error(errorMessage)
        }
      }

      if (key === 'resourceType') {
        if (!erUploadResourceTypesAllowed.includes(value as ResourceType)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(
            errorMessage,
            JSON.stringify(erUploadResourceTypesAllowed),
            {
              value,
            },
          )

          throw new Error(errorMessage)
        }
      }

      if (key === 'questionFormat') {
        if (!erUploadQuestionFormatsAllowed.includes(value as QuestionFormat)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(
            errorMessage,
            JSON.stringify(erUploadQuestionFormatsAllowed),
            {
              value,
            },
          )

          throw new Error(errorMessage)
        }
      }

      if (key === 'calculator') {
        if (!['yes', 'no'].includes(value as string)) {
          const errorMessage = `Invalid value for field '${key}' in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, JSON.stringify(['yes', 'no']), {
            value,
          })

          throw new Error(errorMessage)
        }
      }

      if (key === 'questionStartPage') {
        const numValue = Number(value)
        if (isNaN(numValue) || numValue < 0) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }

      if (key === 'questionEndPage') {
        const numValue = Number(value)
        if (isNaN(numValue) || numValue < 0) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }

      if (key === 'answerStartPage') {
        const numValue = Number(value)
        if (isNaN(numValue) || numValue < 0) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }

      if (key === 'answerEndPage') {
        const numValue = Number(value)
        if (isNaN(numValue) || numValue < 0) {
          const errorMessage = `Invalid value for key: ${key} in resource: ${question.paperName} question: ${question.questionNo}`
          console.error(errorMessage, { value })

          throw new Error(errorMessage)
        }
      }
    }

    return true
  })
}

/**
 * Checks if a topic name is valid.
 * @param name The topic name to check.
 * @returns True if the topic name is valid, false otherwise.
 */
export function checkDpTopicNameIsValid(name: string): boolean {
  // allow letters, numbers, spaces, brackets, carets (^), apostrophes ('), equals (=), and single hyphens.
  // hyphens (-) are allowed, but cannot be the first or last character.
  // disallow double hyphens ('--') as they are used to generate topic id and split data into logical parts.
  return /^[a-zA-Z0-9 ()^'=]+(?:-[a-zA-Z0-9 ()^'=]+)*$/.test(name)
}

/**
 * Extracts the total marks from a list of exam questions.
 *
 * @param questions - An array of exam questions, each containing marks with mark values.
 * @returns The total number of marks calculated by summing the marks of all questions in all topics.
 */
export function extractTotalMarksFromExamQuestions(
  questions: IMeilisearchExamQuestion[],
): number {
  return questions.reduce((acc, next) => {
    const marks = Number(next.marks)
    return isNaN(marks) ? acc : acc + marks
  }, 0)
}

export const questionFormatFullTextSwitchObj: {
  [key in QuestionFormat]: string
} = {
  [QuestionFormat.Mcq]: 'Multiple Choice Question',
  [QuestionFormat.Mrq]: 'Multiple Response Question',
  [QuestionFormat.Open]: 'Open-Ended Question',
}

export const questionFormatDescriptionSwitchObj: {
  [key in QuestionFormat]: string
} = {
  [QuestionFormat.Mcq]: 'Single answer',
  [QuestionFormat.Mrq]: 'Multiple answers',
  [QuestionFormat.Open]: 'Text answer',
}
